import L from "leaflet";
import type { RouteContext } from "../routing";
import type { ParsedPoi } from "./types";
import {
  getMinHours,
  getMaxHours,
  getRadiusKm,
  isIncludeToilets,
} from "../dom";
import { buildBufferPolygon, simplifyPolygon, polyToOverpassString, polygonToLatLngs } from "../geo";
import { buildOverpassQuery } from "./query";
import { parsePoiResults } from "./parse";
import { rerenderPoiList, rerenderMarkers } from "./render";
import { getVisiblePois } from "./state";

// ─── POI search ──────────────────────────────────────────────────────────────

export async function searchPois(
  routeCtx:    RouteContext,
  onResults:   (pois: ParsedPoi[]) => void,
  setStatus:   (msg: string, kind?: "" | "error" | "ok") => void,
): Promise<void> {
  const minH     = getMinHours();
  const maxH     = getMaxHours();
  const radiusKm = getRadiusKm();

  if (maxH <= minH) { setStatus("Max stop time must be greater than min.", "error"); return; }

  const { routeSegment } = await import("../geo");
  const segment = routeSegment(routeCtx.geometry, routeCtx.durationS, minH * 3600, maxH * 3600);
  if (segment.length < 2) { setStatus("Route segment too short for POI search.", "error"); return; }

  setStatus("Searching POIs…", "");

  const buffer     = buildBufferPolygon(segment, radiusKm * 1000);
  const simplified  = simplifyPolygon(buffer);
  const polyStr    = polyToOverpassString(simplified);
  const query      = buildOverpassQuery(polyStr, isIncludeToilets());

  let res: Response;
  try {
    res = await fetch("https://overpass-api.de/api/interpreter", {
      method: "POST",
      body: `data=${encodeURIComponent(query)}`,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
  } catch (err) {
    setStatus(`Network error: ${err}`, "error");
    return;
  }

  if (!res.ok) { setStatus(`Overpass error: ${res.status}`, "error"); return; }

  const json = await res.json();
  const pois = parsePoiResults(json, routeCtx, simplified);
  onResults(pois);
  setStatus(`Found ${pois.length} POI(s)`, "ok");
}

// ─── Polygon drawing ──────────────────────────────────────────────────────────

export function drawPolygons(
  full:       GeoJSON.Feature,
  simplified: GeoJSON.Feature,
  map:        L.Map,
): { full: L.Polygon; simplified: L.Polygon } {
  const fullPoly = L.polygon(polygonToLatLngs(full), {
    color: "#ffffff", weight: 0,
    fillColor: "#ffffff", fillOpacity: 0.15,
    interactive: false,
  }).addTo(map);

  const simplPoly = L.polygon(polygonToLatLngs(simplified), {
    color: "#ffffff", weight: 1.5,
    fillColor: "#ffffff", fillOpacity: 0.25,
    interactive: false,
  }).addTo(map);

  return { full: fullPoly, simplified: simplPoly };
}
